import React, { useContext, useEffect, useState } from "react";
import HoverTooltip from "../../components/common/HoverTooltip";
import { Dialog } from "@headlessui/react";
import ModalCloseButton from "../../components/common/ModalCloseButton";
import { AlertTriangle, SlidersHorizontal, Link2, RotateCcw, Save, Info } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AppContext from "../../context/AppContext";
import CommonButton from "../../components/common/CommonButton";
import BackButton from "../../components/common/BackButton";
import ReactSelector from "../../components/common/CommonReactSelector";
import TableScrollShell from "../../components/common/TableScrollShell";
import { apiErrorMessage, getInventoryPermissions, useInventoryScope } from "../../components/InventoryManagement/inventoryFormShared";
import { fetchMappingWorkflows, fetchMappingFields, fetchMappingTargetColumns, fetchColumnMappings, saveColumnMappings, setColumnMappingStatus, deleteColumnMapping, eligibleWorkflows, mappingList, targetColumns } from "../../service/InventoryColumnMapping";
import { isRTL } from "../../utils/rtl";
import { toast } from "../../utils/toast";
import translations from "./mappingTranslations";

function HeaderInfoTooltip({ text }) {
  return (
    <div className="relative inline-flex items-center">
      <HoverTooltip text={text} position="bottom">
        <button
          type="button"
          className="inline-flex items-center text-slate-400 hover:text-[#236DB4] dark:hover:text-blue-400 transition-colors focus:outline-none cursor-help p-0.5"
          aria-label={text}
        >
          <Info size={13} className="shrink-0" aria-hidden="true" />
        </button>
      </HoverTooltip>
    </div>
  );
}

const rowId = (row) => row.id ?? row.mappingId ?? row.columnMappingId;
const asBoolean = (value, fallback = false) => value == null ? fallback : value === true || value === "true";
const mappingActive = (mapping) => asBoolean(mapping?.isActive ?? mapping?.active, true);
const selectorProps = { isSearchable: true, menuPosition: "fixed", height: "42px", borderRadius: "8px", fontSize: "14px" };
// Target columns that take several workflow questions at once (Seal 1, Seal 2, ...
// all feed New Meter -> Seal Numbers). Their mappings are keyed per question.
const MULTI_FIELD_COLUMNS = new Set(["sealNumbers"]);
const isMultiColumn = (column) => MULTI_FIELD_COLUMNS.has(column);
const mappingKey = (mapping) => JSON.stringify(isMultiColumn(mapping.targetColumn)
  ? [mapping.itemRole, mapping.targetColumn, String(mapping.workflowFieldId)]
  : [mapping.itemRole, mapping.targetColumn]);
const rowMapped = (row) => row.multi ? row.workflowFieldIds.length > 0 : !!row.workflowFieldId;

export default function InventoryConfiguration() {
  const scope = useInventoryScope();
  // Remount on scope changes so a previous organization's pending responses and
  // drafts can never be used to write mappings for the new organization.
  return <Configuration key={`${scope.organizationId}:${scope.projectId}`} {...scope} />;
}

function Configuration({ organizationId, projectId }) {
  const { theme, langMode } = useContext(AppContext);
  const t = translations[langMode] || translations.en;
  const navigate = useNavigate();
  const permissions = getInventoryPermissions();
  const [workflows, setWorkflows] = useState([]);
  const [workflow, setWorkflow] = useState("");
  const [role, setRole] = useState("NEW_METER");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const roles = [{ value: "NEW_METER", label: t.newMeter }, { value: "OLD_METER", label: t.oldMeter }, { value: "MODEM", label: t.modem }, { value: "SIM_CARD", label: t.sim }];
  const card = theme === "dark" ? "bg-slate-900 text-slate-100 border-slate-600" : theme === "high-contrast" ? "bg-black text-white border-white" : "bg-white text-slate-800 border-slate-200";
  const [pendingAction, setPendingAction] = useState(null);
  const requestDiscard = (action) => {
    if (busy) return;
    if (dirty) setPendingAction(() => action);
    else action();
  };
  const cancelDiscard = () => setPendingAction(null);
  const confirmDiscard = () => {
    const action = pendingAction;
    setPendingAction(null);
    setDirty(false);
    action?.();
  };

  useEffect(() => {
    if (!permissions.canView || !organizationId || !projectId) return;
    const controller = new AbortController();
    setLoading(true); setError("");
    fetchMappingWorkflows({ projectId: Number(projectId), organizationId: Number(organizationId) }, controller.signal)
      .then((data) => { if (!controller.signal.aborted) setWorkflows(eligibleWorkflows(data)); })
      .catch((err) => { if (!controller.signal.aborted) setError(apiErrorMessage(err, t.failed)); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [organizationId, projectId, permissions.canView, revision, t.failed]);

  useEffect(() => {
    if (!dirty) return;
    const warn = (event) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  return <div dir={isRTL(langMode) ? "rtl" : "ltr"} className={`w-full min-w-0 max-w-full space-y-3 p-2 sm:p-3 ${theme === "dark" ? "dark-mode bg-slate-900 text-white" : theme === "high-contrast" ? "high-contrast bg-black text-white" : "bg-slate-50 text-slate-800"}`} data-testid="inventory-configuration">
    <div className="flex min-h-[48px] min-w-0 shrink-0 items-center gap-2">
      <fieldset disabled={busy} className="m-0 shrink-0 border-0 p-0 disabled:opacity-50 [&_button]:min-h-[40px] [&_button]:min-w-[40px] sm:[&_button]:min-h-[36px] sm:[&_button]:min-w-[36px]">
        <BackButton path="/inventory-management" onClick={() => requestDiscard(() => navigate("/inventory-management"))} />
      </fieldset>
      <h1 className="min-w-0 break-words text-lg font-semibold">{t.title}</h1>
    </div>
    <section className={`rounded-xl border p-4 sm:p-5 ${card}`}>
      <div className="flex items-start gap-3"><span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#236DB4]/10 text-[#236DB4]"><SlidersHorizontal size={20} aria-hidden="true" /></span><div className="min-w-0"><h2 className="text-base font-semibold">{t.setup}</h2><p className={`mt-1 text-sm ${theme === "dark" ? "text-slate-300" : theme === "high-contrast" ? "text-white" : "text-slate-500"}`}>{t.subtitle}</p></div></div>
      {!permissions.canView ? <p className="mt-4" role="alert">{t.noAccess}</p> : !organizationId || !projectId ? <p className="mt-4">{t.scope}</p> : <>
        <div className="mt-5 grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="min-w-0"><label htmlFor="mapping-workflow" className="mb-2 block text-sm font-medium">{t.workflow}</label>
            <ReactSelector {...selectorProps} dataTestId="mapping-workflow" options={workflows} value={workflows.find((option) => option.value === workflow) || null} isDisabled={busy || loading || !!error} placeholder={loading ? t.loading : t.choose} onChange={(option) => { if (option?.value !== workflow) requestDiscard(() => { setWorkflow(option?.value || ""); setDirty(false); }); }} />
          </div>
          <div className="min-w-0"><label htmlFor="mapping-role" className="mb-2 block text-sm font-medium">{t.role}</label>
            <ReactSelector {...selectorProps} dataTestId="mapping-role" options={roles} value={roles.find((option) => option.value === role)} isDisabled={busy || !workflow} onChange={(option) => { if (option && option.value !== role) requestDiscard(() => { setRole(option.value); setDirty(false); }); }} />
          </div>
        </div>
        {error ? <div className="mt-4" role="alert"><p>{error}</p><CommonButton colorIntent="primary" theme={theme} variant="outline" onClick={() => setRevision((value) => value + 1)}>{t.retry}</CommonButton></div> : !loading && workflows.length === 0 ? <p className="mt-4">{t.empty}</p> : null}
      </>}
    </section>
    {permissions.canView && workflow && <MappingEditor key={`${workflow}:${role}`} {...{ workflow, role, roles, organizationId, projectId, permissions, theme, t, card, dirty, setDirty, busy, setBusy, requestDiscard }} />}
    <Dialog open={pendingAction !== null} onClose={cancelDiscard} className="relative z-[150]" dir={isRTL(langMode) ? "rtl" : "ltr"}>
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className={`relative max-h-[90dvh] w-full max-w-md overflow-y-auto rounded-2xl border p-5 shadow-xl sm:p-6 ${card}`}>
          <ModalCloseButton onClick={cancelDiscard} ariaLabel={t.cancel} className="absolute end-3 top-3 !h-10 !w-10" />
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-700"><AlertTriangle size={24} aria-hidden="true" /></div>
          <Dialog.Title className="mt-4 text-lg font-semibold">{t.discard}</Dialog.Title>
          <Dialog.Description className="mt-2 text-sm leading-relaxed opacity-80">{t.discardDescription}</Dialog.Description>
          <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <CommonButton theme={theme} variant="outline" onClick={cancelDiscard} className="!min-h-[40px] w-full">{t.keepEditing}</CommonButton>
            <CommonButton colorIntent="danger" theme={theme} variant="danger" onClick={confirmDiscard} className="!min-h-[40px] w-full">{t.discardChanges}</CommonButton>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  </div>;
}

function MappingEditor({ workflow, role, roles = [], organizationId, projectId, permissions, theme, t, card, dirty, setDirty, busy, setBusy, requestDiscard }) {
  const { langMode } = useContext(AppContext);
  const [activeTab, setActiveTab] = useState("edit");
  const [savedRoleFilter, setSavedRoleFilter] = useState("ALL");
  const [fields, setFields] = useState([]);
  const [rows, setRows] = useState([]);
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const [deleting, setDeleting] = useState(null);
  const [edited, setEdited] = useState({});
  const deletingItem = saved.find((m) => rowId(m) === deleting);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError(""); setDeleting(null);
    const scope = { organizationId: Number(organizationId), workflowId: Number(workflow) };
    Promise.all([
      fetchMappingFields(scope, controller.signal),
      fetchMappingTargetColumns({ ...scope, projectId: Number(projectId), itemRole: role }, controller.signal),
      fetchColumnMappings(scope, controller.signal),
    ]).then(([fieldData, columnData, mappingData]) => {
      if (controller.signal.aborted) return;
      const options = mappingList(fieldData, "fields").map((field) => ({ value: String(field.workflowFieldId ?? field.fieldId ?? field.id), label: field.question ?? field.fieldLabel ?? field.fieldName ?? field.label ?? field.name ?? String(field.workflowFieldId ?? field.id) })).filter((field) => field.value !== "undefined");
      const mappings = mappingList(mappingData, "mappings");
      const columns = targetColumns(columnData, role);
      if (new Set(columns.map((column) => column.value)).size !== columns.length) throw new Error(t.duplicate);
      setFields(options); setSaved(mappings.map((mapping) => ({ ...mapping, isActive: mappingActive(mapping) })));
      setRows(columns.map((column) => {
        if (isMultiColumn(column.value)) {
          const existingAll = mappings.filter((mapping) => mapping.itemRole === role && mapping.targetColumn === column.value);
          return { targetColumn: column.value, label: column.label, multi: true, workflowFieldIds: existingAll.map((mapping) => String(mapping.workflowFieldId)), workflowFieldId: "", ischeck_meter_number: false, isActive: existingAll.length ? existingAll.some(mappingActive) : true, persisted: existingAll.length > 0 };
        }
        const existing = mappings.find((mapping) => mapping.itemRole === role && mapping.targetColumn === column.value);
        return { targetColumn: column.value, label: column.label, workflowFieldId: existing ? String(existing.workflowFieldId) : "", ischeck_meter_number: asBoolean(existing?.ischeck_meter_number), isActive: mappingActive(existing), persisted: !!existing };
      }));
      setEdited({}); setDirty(false);
    }).catch((err) => { if (!controller.signal.aborted) setError(apiErrorMessage(err, t.failed)); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [workflow, role, projectId, organizationId, revision, setDirty, t.failed, t.duplicate]);

  const update = (column, changes) => {
    setRows((current) => current.map((row) => row.targetColumn === column ? { ...row, ...changes } : row));
    setEdited((current) => ({ ...current, [column]: true })); setDirty(true);
  };
  const reload = () => requestDiscard(() => setRevision((value) => value + 1));
  const mutate = async (action, message) => {
    setBusy(true);
    try { await action(); if (message) toast.success(message); setDirty(false); setRevision((value) => value + 1); }
    catch (err) { toast.error(apiErrorMessage(err, t.failed)); }
    finally { setBusy(false); }
  };
  const toggleStatus = async (mapping) => {
    if (busy || !permissions.canUpdate || rowId(mapping) == null) return;
    const isActive = !mappingActive(mapping);
    setBusy(true);
    try {
      await setColumnMappingStatus(rowId(mapping), isActive, Number(organizationId));
      // Status is an independent operation: keep any unsaved question mappings.
      setSaved((current) => current.map((item) => rowId(item) === rowId(mapping) ? { ...item, isActive } : item));
      if (mapping.itemRole === role) {
        setRows((current) => current.map((row) => row.targetColumn === mapping.targetColumn ? { ...row, isActive } : row));
      }
    } catch (err) {
      toast.error(apiErrorMessage(err, t.failed));
    } finally {
      setBusy(false);
    }
  };
  // A multi-question row counts as a change even when emptied, since that removes
  // its questions from the saved set.
  const savableRows = rows.filter((row) => edited[row.targetColumn] && (row.multi ? row.persisted || row.workflowFieldIds.length : row.workflowFieldId));
  const save = () => {
    const mappings = savableRows;
    if (!mappings.length) { toast.warn(t.required); return; }
    if (mappings.some((row) => row.persisted ? !permissions.canUpdate : !permissions.canCreate)) return;
    mutate(async () => {
      // Save replaces the workflow's mapping set. Read the latest complete set
      // and replace only edited role/column pairs, including other roles intact.
      const latest = mappingList(await fetchColumnMappings({ workflowId: Number(workflow), organizationId: Number(organizationId) }), "mappings");
      const merged = new Map(latest.map((mapping) => [mappingKey(mapping), mapping]));
      mappings.forEach((mapping) => {
        if (mapping.multi) {
          // The selection is the whole set for this column: drop what was saved, add each question.
          Array.from(merged.keys()).forEach((key) => {
            const saved = merged.get(key);
            if (saved.itemRole === role && saved.targetColumn === mapping.targetColumn) merged.delete(key);
          });
          mapping.workflowFieldIds.forEach((workflowFieldId) => {
            const updated = { itemRole: role, targetColumn: mapping.targetColumn, workflowFieldId, ischeck_meter_number: false, isActive: mapping.isActive };
            merged.set(mappingKey(updated), updated);
          });
          return;
        }
        const updated = { ...mapping, itemRole: role };
        merged.set(mappingKey(updated), updated);
      });
      await saveColumnMappings({
        projectId: Number(projectId), workflowId: Number(workflow), organizationId: Number(organizationId),
        mappings: Array.from(merged.values(), (mapping) => ({
          workflowFieldId: Number(mapping.workflowFieldId), itemRole: mapping.itemRole,
          targetColumn: mapping.targetColumn,
          ischeck_meter_number: asBoolean(mapping.ischeck_meter_number),
          isActive: mappingActive(mapping),
        })),
      });
    }, t.saved);
  };
  const cell = "px-4 py-3 text-start align-middle";
  const mappedCount = rows.filter(rowMapped).length;
  const muted = theme === "dark" ? "text-slate-300" : theme === "high-contrast" ? "text-white" : "text-slate-500";
  const tableHead = theme === "dark" ? "bg-slate-800 text-slate-200" : theme === "high-contrast" ? "bg-black text-white" : "bg-slate-50 text-slate-600";
  if (loading) return <p role="status" className="p-4">{t.loading}</p>;
  if (error) return <section className={`rounded-xl border p-4 ${card}`} role="alert"><p className="mb-3">{error}</p><CommonButton colorIntent="primary" theme={theme} onClick={reload}>{t.retry}</CommonButton></section>;

  const filteredSaved = savedRoleFilter === "ALL"
    ? saved
    : saved.filter((item) => item.itemRole === savedRoleFilter);

  return <div className="space-y-3">
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-current/10 pb-2">
      <div role="tablist" aria-label="Mapping views" className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          role="tab"
          id="tab-edit"
          aria-selected={activeTab === "edit"}
          aria-controls="panel-edit"
          onClick={() => setActiveTab("edit")}
          className={`flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm font-semibold transition-all ${
            activeTab === "edit"
              ? "bg-[#236DB4] text-white shadow-sm"
              : "bg-current/5 opacity-80 hover:bg-current/10 hover:opacity-100"
          }`}
        >
          <Link2 size={16} aria-hidden="true" />
          <span>{t.mapTab || t.mappingTitle}</span>
          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
            activeTab === "edit" ? "bg-white/20 text-white" : "bg-current/10"
          }`}>
            {mappedCount}/{rows.length}
          </span>
        </button>
        <button
          type="button"
          role="tab"
          id="tab-saved"
          aria-selected={activeTab === "saved"}
          aria-controls="panel-saved"
          onClick={() => setActiveTab("saved")}
          className={`flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm font-semibold transition-all ${
            activeTab === "saved"
              ? "bg-[#236DB4] text-white shadow-sm"
              : "bg-current/5 opacity-80 hover:bg-current/10 hover:opacity-100"
          }`}
        >
          <SlidersHorizontal size={16} aria-hidden="true" />
          <span>{t.savedTab || t.existing}</span>
          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
            activeTab === "saved" ? "bg-white/20 text-white" : "bg-current/10"
          }`}>
            {saved.length}
          </span>
        </button>
      </div>
      <div className="flex items-center gap-2">
        <CommonButton colorIntent="primary" theme={theme} variant="outline" disabled={busy} onClick={reload} className="!min-h-[38px] text-xs">
          <RotateCcw size={14} aria-hidden="true" />
          {t.refresh}
        </CommonButton>
      </div>
    </div>

    {activeTab === "edit" && (
      <section id="panel-edit" role="tabpanel" aria-labelledby="tab-edit" className={`min-w-0 rounded-xl border p-4 sm:p-5 ${card}`}>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#236DB4]/10 text-[#236DB4]">
              <Link2 size={20} aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <h2 className="font-semibold">{t.mappingTitle}</h2>
              <p className={`mt-1 text-xs ${muted}`}>{t.mappingHint}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className={`rounded-full border px-3 py-1 text-xs font-medium ${theme === "light" ? "border-blue-100 bg-blue-50 text-[#236DB4]" : "border-current"}`}>
              {mappedCount} / {rows.length} {t.mapped}
            </span>
          </div>
        </div>

        {saved.length > 0 && (
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[#236DB4]/20 bg-[#236DB4]/5 px-3 py-2 text-xs">
            <span className="flex items-center gap-1.5 font-medium text-[#236DB4]">
              <SlidersHorizontal size={14} aria-hidden="true" />
              {saved.length} {t.existing.toLowerCase()}
            </span>
            <button
              type="button"
              onClick={() => setActiveTab("saved")}
              className="font-semibold text-[#236DB4] underline hover:opacity-80"
            >
              {t.savedTab} &rarr;
            </button>
          </div>
        )}

        {fields.length === 0 && <p className="mb-3">{t.noFields}</p>}
        {rows.length === 0 ? <p>{t.noColumns}</p> : <TableScrollShell theme={theme} className="min-w-0 max-w-full" innerClassName="!shadow-none !rounded-lg !max-h-none !overflow-y-visible">
          <table className="w-full min-w-[740px] table-fixed text-sm">
            <colgroup>
              <col style={{ width: "28%" }} />
              <col style={{ width: "50%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "10%" }} />
            </colgroup>
            <thead className={`sticky top-0 z-10 ${tableHead}`}>
              <tr>
                <th scope="col" className={`${cell} text-xs font-semibold`}>{t.column}</th>
                <th scope="col" className={`${cell} text-xs font-semibold`}>{t.question}</th>
                <th scope="col" className="px-4 py-3 text-center align-middle text-xs font-semibold">
                  <div className="inline-flex items-center justify-center gap-1">
                    <span>{t.check}</span>
                    <HeaderInfoTooltip text={t.checkTooltip} />
                  </div>
                </th>
                <th scope="col" className="px-4 py-3 text-center align-middle text-xs font-semibold">{t.active}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => {
                const disabled = busy || (row.persisted ? !permissions.canUpdate : !permissions.canCreate);
                const asOption = (id) => fields.find((field) => field.value === id) || { value: id, label: `${t.missing} (${id})` };
                const selected = row.multi ? row.workflowFieldIds.map(asOption) : row.workflowFieldId ? asOption(row.workflowFieldId) : null;
                return <tr key={row.targetColumn} className="border-t border-current/10">
                  <td className={`${cell} max-w-[220px] break-words`}><label className="font-medium" htmlFor={`mapping-field-${index}`} title={row.targetColumn}>{row.label}</label>{row.multi && <p className={`mt-0.5 text-xs ${muted}`}>{t.multiHint}</p>}</td>
                  <td className={`${cell} min-w-[280px]`}>{row.multi
                    ? <ReactSelector {...selectorProps} isMulti closeMenuOnSelect={false} menuPortalTarget={document.body} dataTestId={`mapping-field-${index}`} options={fields} value={selected} isDisabled={disabled || !fields.length} isClearable placeholder={t.unmapped} onChange={(options) => update(row.targetColumn, { workflowFieldIds: (options || []).map((option) => option.value) })} />
                    : <ReactSelector {...selectorProps} menuPortalTarget={document.body} dataTestId={`mapping-field-${index}`} options={fields} value={selected} isDisabled={disabled || !fields.length} isClearable={!row.persisted} placeholder={t.unmapped} onChange={(option) => update(row.targetColumn, { workflowFieldId: option?.value || "" })} />}</td>
                  <td className="px-4 py-3 text-center align-middle">
                    {/* Seals are validated by the backend at submit, so the meter-number check does not apply. */}
                    {row.multi ? <span className={`text-xs ${muted}`}>—</span> : <input type="checkbox" className="mx-auto h-5 w-5 cursor-pointer accent-[#236DB4] disabled:cursor-not-allowed disabled:opacity-40" aria-label={`${t.check}: ${row.label}`} checked={row.ischeck_meter_number} disabled={disabled || !row.workflowFieldId} onChange={(event) => update(row.targetColumn, { ischeck_meter_number: event.target.checked })} />}
                  </td>
                  <td className="px-4 py-3 text-center align-middle">
                    <input type="checkbox" className="mx-auto h-5 w-5 cursor-pointer accent-[#236DB4] disabled:cursor-not-allowed disabled:opacity-40" aria-label={`${t.active}: ${row.label}`} checked={row.isActive} disabled={disabled || !rowMapped(row)} onChange={(event) => update(row.targetColumn, { isActive: event.target.checked })} />
                  </td>
                </tr>;
              })}
            </tbody>
          </table>
        </TableScrollShell>}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-current/10 pt-4">
          <span className={`text-xs ${muted}`}>{t.mappingFooter}</span>
          <div className="flex flex-wrap items-center gap-3">
            {dirty && <span className="text-sm" role="status">{t.changed}</span>}
            <CommonButton colorIntent="success" theme={theme} disabled={busy || !dirty || !savableRows.length} onClick={save} className="!min-h-[40px]">
              <Save size={15} aria-hidden="true" />
              {busy ? t.saving : t.save}
            </CommonButton>
          </div>
        </div>
      </section>
    )}

    {activeTab === "saved" && (
      <section id="panel-saved" role="tabpanel" aria-labelledby="tab-saved" className={`min-w-0 rounded-xl border p-4 sm:p-5 ${card}`}>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="flex items-center gap-2 font-semibold">
              <SlidersHorizontal size={18} className="text-[#236DB4]" aria-hidden="true" />
              {t.existing}
              <span className="rounded-full bg-[#236DB4]/10 px-2.5 py-0.5 text-xs font-semibold text-[#236DB4]">
                {saved.length}
              </span>
            </h2>
            <p className={`mt-1 text-xs ${muted}`}>{t.savedSubtitle}</p>
          </div>
          {saved.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 text-xs">
              <span className={`me-1 font-medium ${muted}`}>{t.filterRole}:</span>
              {["ALL", "NEW_METER", "OLD_METER", "MODEM", "SIM_CARD"].map((filterKey) => {
                const count = filterKey === "ALL" ? saved.length : saved.filter((m) => m.itemRole === filterKey).length;
                if (filterKey !== "ALL" && count === 0) return null;
                const opt = roles.find((r) => r.value === filterKey);
                const label = filterKey === "ALL" ? t.allRoles : (opt?.label || filterKey);
                const isSelected = savedRoleFilter === filterKey;
                return (
                  <button
                    key={filterKey}
                    type="button"
                    onClick={() => setSavedRoleFilter(filterKey)}
                    className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                      isSelected
                        ? "bg-[#236DB4] text-white"
                        : "bg-current/5 hover:bg-current/10"
                    }`}
                  >
                    {label} ({count})
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {!saved.length ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-current/20 py-10 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-current/5 text-[#236DB4]">
              <SlidersHorizontal size={24} aria-hidden="true" />
            </span>
            <p className="mt-3 text-sm font-medium">{t.none}</p>
            <p className={`mt-1 max-w-sm text-xs ${muted}`}>{t.mappingHint}</p>
            <CommonButton theme={theme} variant="outline" onClick={() => setActiveTab("edit")} className="mt-4 !min-h-[38px] text-xs">
              <Link2 size={14} aria-hidden="true" />
              {t.goToMap}
            </CommonButton>
          </div>
        ) : filteredSaved.length === 0 ? (
          <p className="py-6 text-center text-sm opacity-80">{t.noColumns}</p>
        ) : (
          <TableScrollShell theme={theme} className="min-w-0 max-w-full" innerClassName="!shadow-none !rounded-lg !max-h-none !overflow-y-visible">
            <table className="w-full min-w-[760px] table-fixed text-sm">
              <colgroup>
                <col style={{ width: "16%" }} />
                <col style={{ width: "24%" }} />
                <col style={{ width: "38%" }} />
                <col style={{ width: "8%" }} />
                <col style={{ width: "14%" }} />
              </colgroup>
              <thead className={`sticky top-0 z-10 ${tableHead}`}>
                <tr>
                  <th scope="col" className={`${cell} text-xs font-semibold`}>{t.roleCol}</th>
                  <th scope="col" className={`${cell} text-xs font-semibold`}>{t.column}</th>
                  <th scope="col" className={`${cell} text-xs font-semibold`}>{t.question}</th>
                  <th scope="col" className="px-4 py-3 text-center align-middle text-xs font-semibold">
                    <div className="inline-flex items-center justify-center gap-1">
                      <span>{t.check}</span>
                      <HeaderInfoTooltip text={t.checkTooltip} />
                    </div>
                  </th>
                  <th scope="col" className="px-4 py-3 text-end align-middle text-xs font-semibold">{t.actions}</th>
                </tr>
              </thead>
              <tbody>
                {filteredSaved.map((mapping, index) => {
                  const active = mappingActive(mapping);
                  const opt = roles.find((r) => r.value === mapping.itemRole);
                  const roleLabel = opt?.label || mapping.itemRole;
                  const questionLabel = fields.find((f) => f.value === String(mapping.workflowFieldId))?.label || `${t.question} #${mapping.workflowFieldId}`;
                  return (
                    <tr key={rowId(mapping) ?? index} className="border-t border-current/10">
                      <td className={cell}>
                        <span className={`inline-block rounded-md px-2 py-0.5 text-xs font-medium ${
                          mapping.itemRole === "NEW_METER"
                            ? "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300"
                            : mapping.itemRole === "OLD_METER"
                            ? "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300"
                            : mapping.itemRole === "MODEM"
                            ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300"
                            : "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                        }`}>
                          {roleLabel}
                        </span>
                      </td>
                      <td className={`${cell} font-semibold break-words`}>{mapping.targetColumn}</td>
                      <td className={`${cell} break-words`}>{questionLabel}</td>
                      <td className="px-4 py-3 text-center align-middle">
                        <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${
                          asBoolean(mapping.ischeck_meter_number)
                            ? "bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300"
                            : "opacity-60"
                        }`}>
                          {asBoolean(mapping.ischeck_meter_number) ? t.yes : t.no}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-end align-middle">
                        <div className="flex items-center justify-end gap-2">
                          <CommonButton colorIntent={active ? "warning" : "success"}
                            theme={theme}
                            variant="outline"
                            disabled={busy || !permissions.canUpdate || rowId(mapping) == null}
                            onClick={() => toggleStatus(mapping)}
                            className="!min-h-[32px] !px-2.5 !py-1 text-xs"
                          >
                            {active ? t.disable : t.enable}
                          </CommonButton>
                          <CommonButton colorIntent="danger"
                            theme={theme}
                            variant="outline"
                            disabled={busy || dirty || !permissions.canDelete || rowId(mapping) == null}
                            onClick={() => setDeleting(rowId(mapping))}
                            className="!min-h-[32px] !px-2.5 !py-1 text-xs !text-red-600 hover:!bg-red-50 dark:!text-red-400 dark:hover:!bg-red-950/30"
                          >
                            {t.remove}
                          </CommonButton>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableScrollShell>
        )}
        <p className="mt-4 text-xs opacity-75">{t.sync}</p>
      </section>
    )}

    <Dialog
      open={deleting !== null}
      onClose={() => setDeleting(null)}
      className="relative z-[150]"
      dir={isRTL(langMode) ? "rtl" : "ltr"}
    >
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className={`relative max-h-[90dvh] w-full max-w-md overflow-y-auto rounded-2xl border p-5 shadow-2xl sm:p-6 ${card}`}>
          <ModalCloseButton onClick={() => setDeleting(null)} ariaLabel={t.cancel} className="absolute end-3 top-3 !h-10 !w-10" />
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-red-600 dark:bg-red-950/70 dark:text-red-400">
            <AlertTriangle size={24} aria-hidden="true" />
          </div>
          <Dialog.Title className="mt-4 text-lg font-semibold">{t.confirm}</Dialog.Title>
          <Dialog.Description className="mt-2 text-sm leading-relaxed opacity-80">
            {t.deleteDesc}
          </Dialog.Description>
          {deletingItem && (
            <div className="mt-4 rounded-xl border border-current/10 bg-current/5 p-3 text-xs">
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold">{deletingItem.targetColumn}</span>
                <span className="opacity-75">
                  {roles.find((r) => r.value === deletingItem.itemRole)?.label || deletingItem.itemRole}
                </span>
              </div>
            </div>
          )}
          <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <CommonButton
              theme={theme}
              variant="outline"
              disabled={busy}
              onClick={() => setDeleting(null)}
              className="!min-h-[40px] w-full sm:w-auto"
            >
              {t.cancel}
            </CommonButton>
            <CommonButton colorIntent="danger"
              theme={theme}
              variant="danger"
              disabled={busy}
              onClick={() => {
                const idToDelete = deleting;
                setDeleting(null);
                mutate(() => deleteColumnMapping(idToDelete, Number(organizationId)));
              }}
              className="!min-h-[40px] w-full sm:w-auto"
            >
              {t.remove}
            </CommonButton>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  </div>;
}
